import type { Revision, RevisionPageDocument, RevisionPageMetaLink } from '../../types';
import { mutations, yToJSON } from '../../utils';
import { isRevisionPageDocument } from './guards';
import { updatePageInRevision } from './updatePageInRevision';

/**
 * Update the canonical meta link of a page.
 * Use `null` to remove the canonical meta link.
 */
export function updatePageCanonicalLink(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    link: RevisionPageMetaLink | null
) {
    updatePageInRevision(revision, pageId, (page) => {
        if (!isRevisionPageDocument(page)) {
            return;
        }

        const metaLinks = (yToJSON(page) as RevisionPageDocument).metaLinks || {};

        const update = {
            ...metaLinks,
        };

        if (link === null) {
            delete update.canonical;
        } else {
            update.canonical = link;
        }

        mutations.set(page, 'metaLinks', update);
    });
}

/**
 * Add an alternate link to a page.
 */
export function addPageAlternateLink(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    link: RevisionPageMetaLink
) {
    updatePageInRevision(revision, pageId, (page) => {
        if (!isRevisionPageDocument(page)) {
            return;
        }

        const metaLinks = (yToJSON(page) as RevisionPageDocument).metaLinks || {};
        const alternates = metaLinks.alternates || [];

        mutations.set(page, 'metaLinks', {
            ...metaLinks,
            alternates: [...alternates, link],
        });
    });
}

/**
 * Remove an alternate link from a page by its ID.
 * Use `null` to remove the canonical meta link.
 */
export function updatePageAlternateLink(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    linkIndex: number,
    link: RevisionPageMetaLink | null
) {
    updatePageInRevision(revision, pageId, (page) => {
        if (!isRevisionPageDocument(page)) {
            return;
        }

        const metaLinks = (yToJSON(page) as RevisionPageDocument).metaLinks || {};
        const alternates = metaLinks.alternates || [];

        mutations.set(page, 'metaLinks', {
            ...metaLinks,
            alternates:
                link === null
                    ? alternates.filter((_, index) => index !== linkIndex)
                    : alternates.map((alternate, index) =>
                          index === linkIndex ? link : alternate
                      ),
        });
    });
}
